import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Search as SearchIcon, Building2, ChevronRight, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { KPI, SectionCard } from "./AdminJobCards";

const inr = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v || 0));
const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—";

const EST_STATUS_CFG = {
  draft: { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: "Draft" },
  prepared: { cls: "bg-[#457b9d]/15 text-[#1d3557] border-[#457b9d]/30", label: "Prepared" },
  approved: { cls: "bg-[#12B76A]/10 text-[#027A48] border-[#12B76A]/25", label: "Approved" },
  partial: { cls: "bg-[#F79009]/15 text-[#B54708] border-[#F79009]/30", label: "Partial" },
  rejected: { cls: "bg-[#e63946]/10 text-[#e63946] border-[#e63946]/25", label: "Rejected" },
  converted: { cls: "bg-[#F1FAEE] text-[#027A48] border-[#12B76A]/25", label: "Converted" },
  expired: { cls: "bg-[#e63946]/10 text-[#e63946] border-[#e63946]/25", label: "Expired" },
  cancelled: { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: "Cancelled" },
};
const EstChip = ({ s }) => {
  const c = EST_STATUS_CFG[s] || { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: s || "—" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold ${c.cls}`}>{c.label}</span>;
};

export default function AdminEstimates() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [highValueOnly, setHighValueOnly] = useState(false);
  const [sortByValue, setSortByValue] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    const params = { q: q || undefined, status: status || undefined, limit: 150 };
    if (highValueOnly) params.min_value = 100000;
    if (sortByValue) params.sort = "value_desc";
    const [s, l] = await Promise.all([
      api.get("/admin/ops/estimates/summary").then((r) => r.data).catch(() => null),
      api.get("/admin/ops/estimates", { params }).then((r) => r.data).catch(() => ({ rows: [] })),
    ]);
    setSummary(s);
    setRows(l.rows || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, [status, highValueOnly, sortByValue]);

  const kpis = summary?.kpis || {};
  const top = summary?.top_garages || [];
  const funnel = [
    { key: "draft", label: "Draft", n: kpis.draft || 0 },
    { key: "prepared", label: "Prepared", n: kpis.prepared || 0 },
    { key: "approved", label: "Approved", n: kpis.approved || 0 },
    { key: "converted", label: "Converted", n: kpis.converted || 0 },
  ];
  const funnelMax = Math.max(1, ...funnel.map((f) => f.n));

  return (
    <div className="space-y-6" data-testid="admin-estimates-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Platform Ops</div>
        <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Estimates</h1>
        <p className="text-[13px] text-[#667085] mt-0.5">Platform-wide estimate pipeline — track approvals, conversions and lost quotes.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPI testId="est-kpi-total" label="Total" value={kpis.total ?? "—"} Icon={FileText} sub={`+${kpis.this_week ?? 0} this week`} />
        <KPI testId="est-kpi-draft" label="Drafts" value={kpis.draft ?? "—"} tone="steel" />
        <KPI testId="est-kpi-prepared" label="Prepared" value={kpis.prepared ?? "—"} tone="blue" />
        <KPI testId="est-kpi-approved" label="Approved" value={kpis.approved ?? "—"} tone="green" />
        <KPI testId="est-kpi-converted" label="Converted" value={kpis.converted ?? "—"} tone="honeydew" />
        <KPI testId="est-kpi-cancelled" label="Cancelled / Rejected" value={(kpis.cancelled ?? 0) + (kpis.rejected ?? 0)} tone="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <SectionCard
            title="Estimates"
            subtitle={`${rows.length} row(s)`}
            right={
              <div className="flex items-center gap-2">
                <div className="relative">
                  <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#667085]" />
                  <input
                    data-testid="est-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
                    placeholder="EST#, customer, vehicle…"
                    className="pl-7 pr-3 h-8 rounded-md border border-[#E4E7EC] bg-white text-[12px] w-56 focus:outline-none focus:border-[#457b9d]"
                  />
                </div>
                <select
                  data-testid="est-status-filter"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="h-8 px-2 rounded-md border border-[#E4E7EC] bg-white text-[12px] focus:outline-none focus:border-[#457b9d]"
                >
                  <option value="">All statuses</option>
                  <option value="draft">Draft</option>
                  <option value="prepared">Prepared</option>
                  <option value="approved">Approved</option>
                  <option value="partial">Partial</option>
                  <option value="rejected">Rejected</option>
                  <option value="converted">Converted</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button
                  onClick={() => setHighValueOnly((v) => !v)}
                  data-testid="est-high-value-chip"
                  className={`h-8 px-2.5 rounded-md text-[11px] font-bold uppercase tracking-wider border ${highValueOnly ? "bg-[#e63946] text-white border-[#e63946]" : "bg-white text-[#e63946] border-[#e63946]/30 hover:bg-[#e63946]/5"}`}
                >
                  ₹1L+
                </button>
                <button
                  onClick={() => setSortByValue((v) => !v)}
                  data-testid="est-sort-value"
                  className={`h-8 px-2.5 rounded-md text-[11px] font-semibold border ${sortByValue ? "bg-[#1d3557] text-white border-[#1d3557]" : "bg-white text-[#1d3557] border-[#E4E7EC] hover:border-[#1d3557]"}`}
                >
                  Sort ₹↓
                </button>
                <button onClick={reload} className="h-8 px-3 rounded-md bg-[#1d3557] text-white text-[12px] font-semibold hover:bg-[#152941]">Refresh</button>
              </div>
            }
            testId="est-table-card"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-[12px]">
                <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="px-4 py-2 font-semibold">EST #</th>
                    <th className="px-4 py-2 font-semibold">Garage</th>
                    <th className="px-4 py-2 font-semibold">Customer</th>
                    <th className="px-4 py-2 font-semibold">Vehicle</th>
                    <th className="px-4 py-2 font-semibold">Total</th>
                    <th className="px-4 py-2 font-semibold">Status</th>
                    <th className="px-4 py-2 font-semibold">Valid Until</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1FAEE]">
                  {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#667085]">Loading…</td></tr>}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-[#667085]">No estimates match.</td></tr>
                  )}
                  {!loading && rows.map((r) => (
                    <tr key={r.id} data-testid={`est-row-${r.id}`} onClick={() => window.location.assign(`/admin/estimates/${r.id}`)} className="hover:bg-[#F7F9FC] cursor-pointer">
                      <td className="px-4 py-2 font-mono font-semibold text-[#1d3557]">
                        {r.number || <span className="text-[#667085] italic font-sans font-normal">#{(r.id || "").slice(0, 8)}</span>}
                      </td>
                      <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                        <Link to={`/admin/garages/${r.garage_id}`} className="text-[#457b9d] hover:underline">{r.garage_name}</Link>
                      </td>
                      <td className="px-4 py-2 text-[#1d3557]">{r.customer_name || "—"}</td>
                      <td className="px-4 py-2 font-mono">{r.vehicle_reg || "—"}</td>
                      <td className="px-4 py-2">
                        <div className="font-mono font-semibold text-[#1d3557]">{inr(r.total)}</div>
                        {r.high_value && <span className="inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider bg-[#e63946]/8 text-[#e63946] border-[#e63946]/30 mt-0.5">High Value</span>}
                      </td>
                      <td className="px-4 py-2"><EstChip s={r.status} /></td>
                      <td className="px-4 py-2 text-[#667085]">{fmtDate(r.valid_until)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4">
          <SectionCard title="Funnel" subtitle="Draft → Converted" testId="est-funnel">
            <div className="p-5 space-y-3">
              {funnel.map((f) => (
                <div key={f.key}>
                  <div className="flex items-center justify-between text-[12px] mb-1">
                    <span className="font-semibold text-[#1d3557]">{f.label}</span>
                    <span className="font-mono font-bold text-[#1d3557]">{f.n}</span>
                  </div>
                  <div className="h-2 bg-[#F1FAEE] rounded">
                    <div className="h-full bg-[#457b9d] rounded" style={{ width: `${(f.n / funnelMax) * 100}%` }} />
                  </div>
                </div>
              ))}
              {kpis.total > 0 && (
                <div className="text-[11px] text-[#667085] pt-2 flex items-center gap-1">
                  <TrendingUp size={12} className="text-[#12B76A]" />
                  <span>Convert rate: <strong className="text-[#1d3557] font-mono">{Math.round(((kpis.converted || 0) / kpis.total) * 100)}%</strong></span>
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard title="Top Garages" testId="est-top-garages">
            <div className="divide-y divide-[#F1FAEE]">
              {top.length === 0 && <div className="p-6 text-center text-[13px] text-[#667085]">No data</div>}
              {top.map((g, i) => (
                <Link key={g.garage_id} to={`/admin/garages/${g.garage_id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-[#F7F9FC]">
                  <div className="w-7 h-7 rounded-md grid place-items-center bg-[#F1FAEE] text-[#457b9d]"><Building2 size={14} /></div>
                  <div className="flex-1 min-w-0 text-[13px] font-semibold text-[#1d3557] truncate">#{i + 1} {g.garage_name}</div>
                  <div className="text-[13px] font-mono font-bold text-[#1d3557]">{g.estimates_count}</div>
                  <ChevronRight size={13} className="text-[#a8dadc]" />
                </Link>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
